// API failures and the messages the console shows for them. Pure: no DOM.
import type { AdminSchemas } from "@10twenty/api/admin";

/** A failed admin API call, carrying the RFC 9457 problem body when there is one. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly problem: Partial<AdminSchemas["Problem"]> | undefined,
    /** Seconds from `Retry-After`, when sent. */
    readonly retryAfter?: number,
  ) {
    super(problem?.detail ?? problem?.title ?? `Request failed (HTTP ${status})`);
    this.name = "ApiError";
  }

  get code(): string | undefined {
    return this.problem?.code;
  }
}

/** Returns `data` from an openapi-fetch result, throwing `ApiError` on failure. */
export function unwrap<T>(result: { data?: T; error?: unknown; response: Response }): T {
  if (result.error !== undefined || result.data === undefined) {
    const problem =
      typeof result.error === "object" && result.error !== null
        ? (result.error as Partial<AdminSchemas["Problem"]>)
        : undefined;
    const retry = Number(result.response.headers.get("Retry-After") ?? NaN);
    throw new ApiError(result.response.status, problem, Number.isFinite(retry) ? retry : undefined);
  }
  return result.data;
}

/** The browser's passkey prompt was dismissed or timed out. */
export function isPasskeyCancelled(error: unknown): boolean {
  return error instanceof Error && (error.name === "NotAllowedError" || error.name === "AbortError");
}

const CODE_TEXT: Record<string, string> = {
  invalid_enrolment:
    "This link has expired or was already used, or the email or password is wrong. Links last 15 minutes and stop working after 5 wrong passwords; ask an owner for a new one.",
  passkey_exists: "That passkey is already registered here. Sign in with it instead.",
  invalid_passkey: "That passkey isn't registered for the admin console, or it couldn't be verified.",
  invalid_challenge: "That took too long. Try again.",
  not_staff: "This account no longer has a staff role.",
  session_revoked: "Your session has ended. Sign in again.",
  invalid_refresh_token: "Your session has ended. Sign in again.",
  insufficient_role: "Your role can't do this.",
  not_found: "Not found: it may have been deleted.",
  own_account: "You can't do this to your own account.",
  confirmation_mismatch: "That isn't the account's email address.",
  concurrent_change: "The account changed meanwhile. Try again.",
  not_about_message: "This report isn't about a message that still exists.",
  report_closed: "This report was already closed with an action.",
};

/** A message for the operator. */
export function errorText(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 429) {
      return error.retryAfter === undefined
        ? "Too many attempts. Wait a minute and try again."
        : `Too many attempts. Try again in ${Math.max(1, Math.ceil(error.retryAfter))} s.`;
    }
    const known = error.code === undefined ? undefined : CODE_TEXT[error.code];
    return known ?? error.message;
  }
  if (isPasskeyCancelled(error)) return "The passkey prompt was closed or timed out.";
  if (error instanceof TypeError) return "Can't reach the server. Check your connection.";
  return error instanceof Error ? error.message : "Something went wrong.";
}
